<script setup lang="ts">
import Button from '../design-system/button.vue';
import {
  siteModalsStrings,
  AccountModalsStrings,
  type Modals,
  type AccountModals,
} from '~/interfaces/dto/general/modals';
import Drawer from '../design-system/drawer.vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const siteModals = siteModalsStrings;
const accountModals = AccountModalsStrings;

const active = ref<boolean>(false);

const site = useSiteStore();
const auth = useAuthStore();

function openSiteModal(modal: Modals) {
  if (site.getCurrentModal === modal) {
    site.deactivateModal();
    return;
  }
  if (site.getModalActive) site.deactivateModal();
  if (!!auth.getAccountModalPage) auth.toggleAccountModal(false);
  site.activateModal(modal);
}

function openAccModal(modal: AccountModals) {
  if (auth.getAccountModalPage === modal) {
    auth.toggleAccountModal(false);
    return;
  }
  if (!!auth.getAccountModalPage) auth.toggleAccountModal(false);
  if (site.getModalActive) site.deactivateModal();
  auth.setAccountModalPage(modal);
  auth.toggleAccountModal(true);
}

function closeAllModals() {
  site.deactivateModal();
  auth.toggleAccountModal(false);
}

function emergencyModalClose(event: KeyboardEvent) {
  if (event.shiftKey && event.key === 'Z') closeAllModals();
}

onMounted(() => {
  window.addEventListener('keydown', emergencyModalClose);
});

onUnmounted(() => {
  window.removeEventListener('keydown', emergencyModalClose);
});
</script>
<template>
  <div class="flex gap-2">
    <Drawer
      v-model:visible="active"
      position="bottom"
      custom-container-class="bg-base-secondary overflow-auto border-none"
    >
      <template #activator>
        <Button
          class="font-normal w-full justify-center"
          padding="md"
          rounding="md"
          @click="active = true"
        >
          Toggle Modals
        </Button>
      </template>
      <template #header>
        <div class="bg-layer flex justify-between items-center w-full p-4">
          <div class="flex gap-3 z-[100]">
            <p class="text-base-priority text-lg font-bold">Modals</p>
            <Button
              type="tertiary-alternative"
              padding="wide-short"
              rounding="md"
              @click="closeAllModals()"
            >
              Clear Modals
            </Button>
            <p class="text-xs text-base-priority leading-8">
              (Press Shift+Z if Modals get stuck when opening to close)
            </p>
          </div>
          <Button
            element-name="hamburger-menu-close"
            type="tertiary-alternative"
            shape="square"
            class=""
            @click="active = false"
          >
            <XMarkIcon />
          </Button>
        </div>
      </template>
      <div
        class="text-base-priority grid grid-cols-5 gap-2 max-h-[65vh] overflow-scroll no-scrollbar p-2"
      >
        <div class="bg-layer p-2 rounded-lg col-span-3">
          <div class="font-bold">Site Modals</div>
          <hr class="mb-4 stacked-border-color" />
          <div class="flex flex-wrap gap-2">
            <div
              v-for="modal in siteModals"
              :key="'site-' + modal"
              class="flex justify-between mb-1 w-1/3"
            >
              <Button
                class="font-normal stacked-border w-full"
                :type="site.getCurrentModal === modal ? 'primary' : 'tertiary'"
                padding="md"
                rounding="md"
                :title="modal"
                @click="openSiteModal(modal)"
              >
                <span class="w-full truncate">{{ modal }}</span>
              </Button>
            </div>
          </div>
        </div>
        <div class="bg-layer p-2 rounded-lg col-span-2">
          <div class="font-bold">Account Modals</div>
          <hr class="mb-4 stacked-border-color" />
          <div class="grid grid-cols-2 grid-flow-col grid-rows-9">
            <div
              v-for="modal in accountModals"
              :key="'acc-' + modal"
              class="flex justify-between mb-1"
            >
              <Button
                class="font-normal stacked-border w-full"
                :type="auth.accountModalPage === modal ? 'primary' : 'tertiary'"
                padding="md"
                rounding="md"
                :title="modal"
                @click="openAccModal(modal)"
              >
                <span class="w-full truncate">{{ modal }}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>
